import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputBase,
  LinearProgress,
  Popover,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, withStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { capitalize } from 'lodash';
import { DataGrid, GridCellParams, GridColumns } from '@material-ui/data-grid';
import {
  ArrowDownward,
  Email as EmailIcon,
  ExpandMore,
  GetApp,
  Refresh,
  Search,
  PersonAdd as PersonAddIcon,
  Warning,
} from '@material-ui/icons';
import Icon from '@mdi/react';
import { mdiCursorDefaultClick } from '@mdi/js';
import { DateTime } from 'luxon';
import { withRouter, useHistory, useLocation } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { TooltipMenu } from '../../../../common/tooltip-menu';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { GET_PROGRAMS_FOR_INCOMING_VOLS_FILTER } from './incoming-volunteers-model.gql';
import { GetProgramsForIncomingVolsFilter } from './__generated__/GetProgramsForIncomingVolsFilter';
import { useUser } from '../../../../global/auth/useUser';
import { StatusChip } from '../../../../common/chips/StatusChip';
import { AvatarName } from '../../../../common/AvatarName';
import { IncomingVolunteerViewApplicantScreen } from '../incoming-volunteer/IncomingVolunteerViewApplicantScreen';
import { IncomingVolunteerViewInviteeScreen } from '../incoming-volunteer/IncomingVolunteerViewInviteeScreen';
import { IncomingVolunteerViewProfileScreen } from '../incoming-volunteer/IncomingVolunteerViewProfileScreen';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { ExportsList } from '../../../../common/export/ExportsList';
import { useVolunteersContext } from '../VolunteersContext';
import { TaskBadge } from '../../../admin/tasks/TaskBadge';
import { useRequestExport } from '../../../../common/export/Export';
import { TaskFieldSearchDialog, TaskFieldSearchParams } from '../volunteer-database/TaskFieldSearchDialog';
import { GET_TASKS_WITH_FIELDS } from '../../../admin/tasks-import/get-tasks-with-fields.gql';
import { GetTasksWithFields } from '../../../admin/tasks-import/__generated__/GetTasksWithFields';
import { GET_PROGRAM_ACTIVITIES_VOL_DB } from '../../../../global/programs-sell/program-activites.gql';
import { GetProgramActivitiesVolDB } from '../../../../global/programs-sell/__generated__/GetProgramActivitiesVolDB';

interface IncomingVolunteersArray {
  total: number;
  incomingVolunteers: IncomingVolunteerSimple[];
}

interface IncomingVolunteerSimple {
  incomingVolunteerId: string;
  name: string | null;
  email: string;
  source: string;
  isInvited: boolean;
  lastUpdated: string;
  status: string;
  percentageComplete: number;
  isCompleted: boolean;
  isProfileTaskApproved: boolean;
  hasPendingTasks: boolean;
  hasScreeningReview: boolean;
  applicationId: string | null;
  inviteeId: string | null;
  profileId: string | null;
  userId: string | null;
  programs: Array<{ programId: string; name: string }>;
  dateRevoked: string | null;
  applicationStatus: string | null;
  reminderSentAt: string | null;
  avatarUrl?: string | null;
  firstName?: string;
  lastName?: string;
  preferredName?: string | null;
  commentCount: number;
}

const useStyles = makeStyles((theme: any) =>
  createStyles({
    hideRightSeparator: {
      '.MuiDataGrid-columnSeparator': {
        display: 'none !important',
      },
    },
    edit: {
      color: theme.alert.blue.extraLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      '& span': {
        fontSize: '14px',
      },
      '& p': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
      textTransform: 'none',
    },
  })
);

export const BorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 10,
    borderRadius: 5,
    width: '100%',
  },
  colorPrimary: {
    backgroundColor: theme.palette.grey[theme.palette.type === 'light' ? 200 : 700],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: theme.color.primary,
  },
}))(LinearProgress);

const IncomingVolunteersScreenSimple = withRouter(() => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const history = useHistory();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();

  const [selectedInviteeId, setSelectedInviteeId] = useQueryParam('inviteeId', StringParam);
  const [selectedApplicationId, setSelectedApplicationId] = useQueryParam('applicationId', StringParam);
  const [selectedVolunteerProfileId, setSelectedVolunteerProfileId] = useQueryParam('volunteerProfileId', StringParam);

  const [exportsPopoverAnchor, setExportsPopoverAnchor] = React.useState<HTMLElement | null>(null);

  const [incomingVolunteersData, setIncomingVolunteersData] = useState<IncomingVolunteersArray | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filterString, setFilterString] = useState<string>('');

  const [openEntry, setOpenEntry] = React.useState<boolean>(false);
  const [openStatus, setOpenStatus] = React.useState<boolean>(false);
  const [openPrograms, setOpenPrograms] = React.useState<boolean>(false);
  const [openMoreFilters, setOpenMoreFilters] = React.useState<boolean>(false);
  const [moreFiltersTab, setMoreFiltersTab] = React.useState<'activities' | 'roles' | 'tasks'>('activities');

  const [entryFilter, setEntryFilter] = React.useState<string>('all');
  const [statusFilter, setStatusFilter] = React.useState<string>('active');
  const [programFilter, setProgramFilter] = React.useState<Array<string>>([]);
  const [activityFilter, setActivityFilter] = React.useState<Array<string>>([]);
  const [roleFilter, setRoleFilter] = React.useState<Array<string>>([]);
  const [orderBy, setOrderBy] = React.useState<string>('lastUpdated');
  const [programSearchQuery, setProgramSearchQuery] = React.useState<string>('');
  const [activitySearchQuery, setActivitySearchQuery] = React.useState<string>('');
  const [roleSearchQuery, setRoleSearchQuery] = React.useState<string>('');
  const [actionsEnabled, setActionsEnabled] = React.useState<boolean>(false);
  const [taskFieldFilter, setTaskFieldFilter] = useState<TaskFieldSearchParams | null>(null);
  const [taskFieldSearchOpen, setTaskFieldSearchOpen] = useState<boolean>(false);

  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });
  const campfireExport = useRequestExport();

  const { data: programActivityData } = useCampfireQuery<GetProgramsForIncomingVolsFilter, undefined>(
    GET_PROGRAMS_FOR_INCOMING_VOLS_FILTER
  );

  const { data: tasksWithFieldsData } = useCampfireQuery<GetTasksWithFields, undefined>(GET_TASKS_WITH_FIELDS);

  const { data: activitiesRolesData } = useCampfireQuery<GetProgramActivitiesVolDB, undefined>(
    GET_PROGRAM_ACTIVITIES_VOL_DB
  );

  const { getManagerIdentity, user } = useUser();
  const { programManagerIdentities } = getManagerIdentity();
  const managedProgramIds = programManagerIdentities.flatMap((p) => p.programId);

  const { setRemindAllDialog } = useVolunteersContext();

  const filteredProgramIds = user.userIdentityService.isProgramManager
    ? programActivityData?.vm.programs.filter((p) => managedProgramIds.includes(p.programId))
    : programActivityData?.vm.programs;

  const searchFilteredPrograms = filteredProgramIds?.filter((program) =>
    program.name.toLowerCase().includes(programSearchQuery.toLowerCase())
  );

  const allActivities = activitiesRolesData?.vm.programs.flatMap((p) => p.activities) ?? [];
  const filteredActivities = user.userIdentityService.isProgramManager
    ? allActivities.filter((a) => managedProgramIds.includes(a.program.programId))
    : allActivities;
  const searchFilteredActivities = filteredActivities.filter((activity) =>
    activity.name.toLowerCase().includes(activitySearchQuery.toLowerCase())
  );

  const allRoles = activitiesRolesData?.vm.activityRoles ?? [];
  const searchFilteredRoles = allRoles.filter((role) =>
    role.name.toLowerCase().includes(roleSearchQuery.toLowerCase())
  );

  function fetch(newPage?: number) {
    setLoading(true);
    setError(null);
    campfireMutation
      .run({
        url: `${apiUrl}/vm/management/incoming-volunteers`,
        method: 'post',
        data: {
          entry: entryFilter === 'all' ? null : entryFilter,
          status: statusFilter === 'all' ? null : statusFilter,
          programs: programFilter,
          activities: activityFilter,
          roles: roleFilter,
          query: filterString,
          sort: orderBy,
          offset: newPage === undefined ? currentPage * 25 : newPage * 25,
          limit: 25,
          ...(taskFieldFilter && {
            taskFieldFilters: taskFieldFilter.filters,
            taskFieldFilterLogic: taskFieldFilter.filterLogic,
          }),
        },
      })
      .then((response) => {
        if (response && response.data && response.data.data) {
          setIncomingVolunteersData(response.data.data as IncomingVolunteersArray);
          setError(null);
        } else {
          setError('Failed to load incoming volunteers data');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Incoming volunteers query error:', err);
        setError('Error loading incoming volunteers. Please try again.');
        setLoading(false);
      });
  }

  function clear() {
    setIncomingVolunteersData(undefined);
  }

  function reset() {
    setEntryFilter('all');
    setStatusFilter('active');
    setProgramFilter([]);
    setActivityFilter([]);
    setRoleFilter([]);
    setFilterString('');
    setTaskFieldFilter(null);
    changePage(0);
    clear();
  }

  useEffect(() => {
    if (!incomingVolunteersData) {
      fetch();
    }
  }, [incomingVolunteersData]);

  function changePage(page: number) {
    setCurrentPage(page);
    fetch(page);
  }

  function programFilterChange(programId: string) {
    if (programFilter.includes(programId)) {
      setProgramFilter(
        programFilter.filter((a) => {
          return a !== programId;
        })
      );
    } else {
      setProgramFilter([...programFilter, programId]);
    }
    changePage(0);
    clear();
  }

  function activityFilterChange(activityId: string) {
    if (activityFilter.includes(activityId)) {
      setActivityFilter(
        activityFilter.filter((a) => {
          return a !== activityId;
        })
      );
    } else {
      setActivityFilter([...activityFilter, activityId]);
    }
    changePage(0);
    clear();
  }

  function roleFilterChange(roleId: string) {
    if (roleFilter.includes(roleId)) {
      setRoleFilter(
        roleFilter.filter((r) => {
          return r !== roleId;
        })
      );
    } else {
      setRoleFilter([...roleFilter, roleId]);
    }
    changePage(0);
    clear();
  }

  function setOrder(type: string) {
    setOrderBy(type);
    clear();
  }

  function handleExportButtonClick(event: React.MouseEvent<HTMLElement>) {
    setExportsPopoverAnchor(event.currentTarget);
  }

  function handleExportsPopoverClose() {
    setExportsPopoverAnchor(null);
  }

  function handleExportRequest() {
    const exportParams = {
      entry: entryFilter === 'all' ? undefined : entryFilter,
      status: statusFilter === 'all' ? undefined : statusFilter,
      program: programFilter.length > 0 ? programFilter : undefined,
      query: filterString || undefined,
    };

    campfireExport('incoming-volunteers', exportParams).then((response) => response.data.data);
  }

  function handleRowClick(row: IncomingVolunteerSimple) {
    let queryParam = '';
    if (row.source === 'Invitee') {
      queryParam = `inviteeId=${row.incomingVolunteerId}`;
    } else if (row.source === 'Application') {
      queryParam = `applicationId=${row.incomingVolunteerId}`;
    } else if (row.source === 'Profile') {
      queryParam = `volunteerProfileId=${row.incomingVolunteerId}`;
    }
    if (queryParam) {
      history.push({ pathname: location.pathname, search: queryParam });
    }
  }

  function handleTaskFieldSearch(params: TaskFieldSearchParams) {
    setTaskFieldFilter(params);
    setTaskFieldSearchOpen(false);
    changePage(0);
    clear();
  }

  const columns: GridColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 350,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return (
          <Box style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Name</Typography>
          </Box>
        );
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const displayName = row.preferredName || row.firstName || row.name || row.email;
        const lastName = row.lastName || '';
        const fullName = `${displayName} ${lastName}`.trim();

        // Determine chip statuses
        const isApplicationDeleted = ['rejected', 'removed'].includes(row.applicationStatus || '');
        const isProfileRemoved = row.profileId && row.status === 'inactive';
        const isInviteeRemoved = row.inviteeId && row.dateRevoked;
        const isAccountRemoved = isApplicationDeleted || isProfileRemoved || isInviteeRemoved;

        // Stage chips - mutually exclusive stages
        const isScreeningStage = row.hasScreeningReview;
        const isProfileStage = row.profileId && !row.isCompleted && !row.hasScreeningReview;
        const isTasksStage = row.isCompleted && !row.hasScreeningReview;
        const isPendingReview = row.status === 'pending_review';
        const isReadyStage = row.status === 'ready';

        return (
          <Box style={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center', width: '100%' }}>
            <AvatarName inTable noTrim avatarUrl={row.avatarUrl} name={fullName || row.email} description={row.email} />
            {isAccountRemoved && <StatusChip tiny status='Removed' bgcolor={theme.status.red.medium} />}
            {isScreeningStage && <StatusChip tiny status='Screening' bgcolor='#64B5F6' />}
            {isProfileStage && <StatusChip tiny status='Profile' bgcolor='#42A5F5' />}
            {isTasksStage && <StatusChip tiny status='Tasks' bgcolor='#2196F3' />}
            {isPendingReview && <StatusChip tiny status='Pending review' bgcolor='#FF9800' />}
            {isReadyStage && <StatusChip tiny status='Ready' bgcolor='#4CAF50' />}
          </Box>
        );
      },
    },
    {
      field: 'comments',
      headerName: 'Comments',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}></Typography>;
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <div style={{ display: 'contents', cursor: 'default' }}>
            <Box style={{ display: 'flex', flex: '1', justifyContent: 'center' }}>
              <svg width='19' height='18' viewBox='0 0 19 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M9.17279 0C14.2387 0 18.3456 3.31835 18.3456 7.41177C18.3456 9.72424 17.0339 11.7889 14.9802 13.1479C15.2472 14.3704 15.8694 16.1836 17.3264 18C14.3921 17.4918 12.5158 16.0089 11.3559 14.6033C10.6547 14.742 9.92649 14.8235 9.17279 14.8235C4.10686 14.8235 0 11.5052 0 7.41177C0 3.31835 4.10686 0 9.17279 0Z'
                  fill='#646464'
                />
              </svg>
            </Box>
            {row.commentCount >= 1 && (
              <Box style={{ position: 'absolute', marginLeft: '22px', marginBottom: '12px' }}>
                <TaskBadge number={row.commentCount} variant='warning' size='small' />
              </Box>
            )}
          </div>
        );
      },
    },
    {
      field: 'progress',
      headerName: 'Progress',
      width: 180,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return (
          <Box
            onClick={() => {
              setOrder('progress');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
          >
            <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Progress</Typography>
            {orderBy === 'progress' ? <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} /> : null}
          </Box>
        );
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <Box width='100%' display='flex' alignItems='center'>
            {row.percentageComplete > 0 ? (
              <>
                <Box flex='1'>
                  <BorderLinearProgress variant='determinate' value={row.percentageComplete * 100} />
                </Box>
                <Typography
                  variant='caption'
                  style={{ fontSize: '12px', color: '#646464', minWidth: '35px', paddingLeft: '4px' }}
                >
                  {Math.round(row.percentageComplete * 100)}%
                </Typography>
              </>
            ) : (
              <Typography variant='caption' color='textSecondary'>
                Not started
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 100,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Source</Typography>;
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const sourceLabel = row.isInvited ? 'Invitee' : 'Applicant';
        const SourceIcon = row.isInvited ? EmailIcon : PersonAddIcon;
        return (
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <SourceIcon style={{ fontSize: '14px', color: '#757575' }} />
            <Typography style={{ fontSize: '12px', color: '#757575' }}>{sourceLabel}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'programs',
      headerName: 'Program(s)',
      width: 200,
      disableColumnMenu: true,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Program(s)</Typography>;
      },
      renderCell: ({ row }: any) => {
        if (row.programs.length === 0) {
          return (
            <Typography noWrap style={{ fontSize: '13px', color: '#646464' }}>
              No programs
            </Typography>
          );
        }
        if (row.programs.length === 1) {
          return (
            <Typography noWrap style={{ fontSize: '13px' }}>
              {row.programs[0].name}
            </Typography>
          );
        }
        return (
          <Typography noWrap style={{ fontSize: '13px' }}>
            {row.programs[0].name} + {row.programs.length - 1} other{row.programs.length > 2 ? 's' : ''}
          </Typography>
        );
      },
    },
    {
      field: 'lastUpdated',
      headerName: 'Last Updated',
      width: 150,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return (
          <Box
            onClick={() => {
              setOrder('lastUpdated');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
          >
            <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Last Updated</Typography>
            {orderBy === 'lastUpdated' ? <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} /> : null}
          </Box>
        );
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const lastUpdatedDate = DateTime.fromISO(row.lastUpdated);
        return <Typography style={{ fontSize: '13px' }}>{lastUpdatedDate.toFormat('d MMM yyyy')}</Typography>;
      },
    },
  ];

  if (selectedInviteeId) {
    return (
      <IncomingVolunteerViewInviteeScreen
        inviteeId={selectedInviteeId}
        goBack={() => {
          setSelectedInviteeId(undefined);
        }}
        reloadList={() => {
          clear();
        }}
      />
    );
  }

  if (selectedApplicationId) {
    return (
      <IncomingVolunteerViewApplicantScreen
        selectedApplicationId={selectedApplicationId}
        goBack={() => {
          setSelectedApplicationId(undefined);
        }}
        reloadList={() => {
          clear();
        }}
      />
    );
  }

  if (selectedVolunteerProfileId) {
    return (
      <IncomingVolunteerViewProfileScreen
        profileId={selectedVolunteerProfileId}
        goBack={() => {
          setSelectedVolunteerProfileId(undefined);
        }}
        reloadList={() => {
          clear();
        }}
        setSnackbar={setSnackbar}
      />
    );
  }

  return (
    <>
      <Box position='relative' display='flex' flexDirection='column' height={1} overflow='auto' bgcolor='#ffffff'>
        {/* Header with Title and Buttons */}
        <Box
          style={{
            paddingLeft: '25px',
            paddingRight: '10px',
            paddingTop: '10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Title */}
          <Typography style={{ fontSize: '14px', fontWeight: 700, color: '#333' }}>Incoming</Typography>

          {/* Buttons Container */}
          <Box style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Button
              onClick={handleExportButtonClick}
              startIcon={<GetApp style={{ fontSize: '14px' }} />}
              style={{
                backgroundColor: 'transparent',
                color: '#646464',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '4.5px 12px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 'auto',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Exports
            </Button>
            <Button
              onClick={() => setActionsEnabled(!actionsEnabled)}
              startIcon={
                <Icon path={mdiCursorDefaultClick} size={0.6} color={actionsEnabled ? '#ffffff' : '#646464'} />
              }
              style={{
                display: 'none',
                backgroundColor: actionsEnabled ? '#646464' : 'transparent',
                color: actionsEnabled ? '#ffffff' : '#646464',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '4.5px 12px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 'auto',
                alignItems: 'center',
              }}
            >
              Actions
            </Button>
            <Button
              onClick={() => setRemindAllDialog(true)}
              startIcon={<EmailIcon style={{ fontSize: '14px' }} />}
              style={{
                backgroundColor: 'transparent',
                color: '#646464',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '4.5px 12px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 'auto',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Send Reminder
            </Button>
          </Box>
        </Box>

        <Box
          style={{
            width: '50%',
            marginBottom: '40px',
            marginTop: '20px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <Box style={{ display: 'flex', justifyContent: 'center' }}>
            <InputBase
              value={filterString}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  clear();
                }
              }}
              placeholder='Search by name or email'
              onChange={(e) => {
                setFilterString(e.target.value);
              }}
              style={{
                border: '.5px solid #d9d9d9',
                borderRadius: '25px 0 0 25px',
                width: '85%',
                display: 'flex',
                padding: '6px 24px',
              }}
            />
            <Button
              style={{
                backgroundColor: '#7047EB',
                borderRadius: '0 25px 25px 0',
                width: '12%',
                display: 'flex',
                padding: '8px 10px',
              }}
              onClick={() => clear()}
            >
              <Search style={{ color: '#fff' }} />
            </Button>
          </Box>
          <Box style={{ display: 'flex', justifyContent: 'center', paddingTop: '12px' }}>
            <TooltipMenu
              open={openStatus}
              onClose={() => {
                setOpenStatus(false);
              }}
              title={
                <RadioGroup
                  aria-label='status'
                  name='status'
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    clear();
                  }}
                >
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='flex-start'
                    style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
                  >
                    <FormControlLabel
                      className={classes.edit}
                      value='all'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='All'
                    />
                    <FormControlLabel
                      className={classes.edit}
                      value='pending'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='Pending'
                    />
                    <FormControlLabel
                      className={classes.edit}
                      value='active'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='Active'
                    />
                    <FormControlLabel
                      className={classes.edit}
                      value='ready'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='Ready'
                    />
                    <FormControlLabel
                      className={classes.edit}
                      value='inactive'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='Inactive'
                    />
                  </Box>
                </RadioGroup>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenStatus(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{capitalize(statusFilter)}</strong>&nbsp;Status
              </TabletButton>
            </TooltipMenu>

            <TooltipMenu
              open={openEntry}
              onClose={() => {
                setOpenEntry(false);
              }}
              title={
                <RadioGroup
                  aria-label='entry'
                  name='entry'
                  value={entryFilter}
                  onChange={(e) => {
                    setEntryFilter(e.target.value);
                    clear();
                  }}
                >
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='flex-start'
                    style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
                  >
                    <FormControlLabel
                      className={classes.edit}
                      value='all'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='All'
                    />
                    <FormControlLabel
                      className={classes.edit}
                      value='applicants'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='Applicants'
                    />
                    <FormControlLabel
                      className={classes.edit}
                      value='invitees'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='Invitees'
                    />
                  </Box>
                </RadioGroup>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenEntry(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{capitalize(entryFilter)}</strong>&nbsp;Sources
              </TabletButton>
            </TooltipMenu>

            <TooltipMenu
              open={openPrograms}
              onClose={() => {
                setOpenPrograms(false);
                setProgramSearchQuery('');
              }}
              title={
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='flex-start'
                  style={{ padding: '5px 10px', justifyContent: 'flex-start', maxHeight: '30vh' }}
                >
                  <Box
                    style={{
                      paddingTop: '6px',
                      paddingBottom: '4px',
                      width: '100%',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <Box style={{ display: 'flex', alignItems: 'center', padding: '4px 0px' }}>
                      <Search style={{ fontSize: '14px', color: '#fff', marginRight: '6px' }} />
                      <InputBase
                        placeholder='Search'
                        value={programSearchQuery}
                        onChange={(e) => setProgramSearchQuery(e.target.value)}
                        style={{ color: '#fff', fontSize: '12px', flex: 1 }}
                      />
                    </Box>
                  </Box>
                  <Box
                    style={{
                      overflowY: 'auto',
                      maxHeight: 'calc(30vh - 40px)',
                      width: '100%',
                    }}
                    sx={{
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {searchFilteredPrograms?.map((program) => {
                      return (
                        <FormControlLabel
                          key={program.programId}
                          className={classes.edit}
                          control={<Checkbox style={{ color: '#fff' }} />}
                          label={program.name}
                          name={program.programId}
                          checked={programFilter && programFilter.includes(program.programId)}
                          onClick={() => programFilterChange(program.programId)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenPrograms(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{programFilter.length === 0 ? 'All' : programFilter.length}</strong>&nbsp;Programs
              </TabletButton>
            </TooltipMenu>

            <TooltipMenu
              open={openMoreFilters}
              onClose={() => {
                setOpenMoreFilters(false);
                setActivitySearchQuery('');
                setRoleSearchQuery('');
              }}
              title={
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='flex-start'
                  style={{ padding: '5px 10px', justifyContent: 'flex-start', maxHeight: '30vh', minWidth: '250px' }}
                >
                  <Box display='flex' style={{ paddingBottom: '8px', width: '100%' }}>
                    <TabletButton
                      size='small'
                      onClick={() => setMoreFiltersTab('activities')}
                      style={{
                        color: moreFiltersTab === 'activities' ? '#fff' : 'rgba(255,255,255,0.6)',
                        fontWeight: moreFiltersTab === 'activities' ? 600 : 'normal',
                        marginRight: '8px',
                        padding: '4px 8px',
                        fontSize: '12px',
                      }}
                    >
                      Activities {activityFilter.length > 0 && `(${activityFilter.length})`}
                    </TabletButton>
                    <TabletButton
                      size='small'
                      onClick={() => setMoreFiltersTab('roles')}
                      style={{
                        color: moreFiltersTab === 'roles' ? '#fff' : 'rgba(255,255,255,0.6)',
                        fontWeight: moreFiltersTab === 'roles' ? 600 : 'normal',
                        marginRight: '8px',
                        padding: '4px 8px',
                        fontSize: '12px',
                      }}
                    >
                      Roles {roleFilter.length > 0 && `(${roleFilter.length})`}
                    </TabletButton>
                    <TabletButton
                      size='small'
                      onClick={() => {
                        setOpenMoreFilters(false);
                        setTaskFieldSearchOpen(true);
                      }}
                      style={{
                        color: taskFieldFilter ? '#fff' : 'rgba(255,255,255,0.6)',
                        fontWeight: taskFieldFilter ? 600 : 'normal',
                        padding: '4px 8px',
                        fontSize: '12px',
                      }}
                    >
                      Tasks {taskFieldFilter && `(${taskFieldFilter.filters.length})`}
                    </TabletButton>
                  </Box>

                  <Box
                    style={{
                      paddingTop: '6px',
                      paddingBottom: '4px',
                      width: '100%',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <Box style={{ display: 'flex', alignItems: 'center', padding: '4px 0px' }}>
                      <Search style={{ fontSize: '14px', color: '#fff', marginRight: '6px' }} />
                      <InputBase
                        placeholder={moreFiltersTab === 'activities' ? 'Search activities' : 'Search roles'}
                        value={moreFiltersTab === 'activities' ? activitySearchQuery : roleSearchQuery}
                        onChange={(e) =>
                          moreFiltersTab === 'activities'
                            ? setActivitySearchQuery(e.target.value)
                            : setRoleSearchQuery(e.target.value)
                        }
                        style={{ color: '#fff', fontSize: '12px', flex: 1 }}
                      />
                    </Box>
                  </Box>
                  <Box
                    style={{
                      overflowY: 'auto',
                      maxHeight: 'calc(30vh - 80px)',
                      width: '100%',
                    }}
                    sx={{
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {moreFiltersTab === 'activities' &&
                      searchFilteredActivities?.map((activity) => (
                        <FormControlLabel
                          key={activity.activityId}
                          className={classes.edit}
                          control={<Checkbox style={{ color: '#fff' }} />}
                          label={activity.name}
                          name={activity.activityId}
                          checked={activityFilter && activityFilter.includes(activity.activityId)}
                          onClick={() => activityFilterChange(activity.activityId)}
                        />
                      ))}
                    {moreFiltersTab === 'roles' &&
                      searchFilteredRoles?.map((role) => (
                        <FormControlLabel
                          key={role.activityRoleId}
                          className={classes.edit}
                          control={<Checkbox style={{ color: '#fff' }} />}
                          label={role.name}
                          name={role.activityRoleId}
                          checked={roleFilter && roleFilter.includes(role.activityRoleId)}
                          onClick={() => roleFilterChange(role.activityRoleId)}
                        />
                      ))}
                  </Box>
                </Box>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenMoreFilters(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color:
                    activityFilter.length > 0 ||
                    roleFilter.length > 0 ||
                    (taskFieldFilter && taskFieldFilter.filters.length > 0)
                      ? theme.color.secondary.main700
                      : theme.color.grey.neutral300,
                  fontWeight:
                    activityFilter.length > 0 ||
                    roleFilter.length > 0 ||
                    (taskFieldFilter && taskFieldFilter.filters.length > 0)
                      ? 600
                      : 'normal',
                }}
              >
                More Filters
                {(activityFilter.length > 0 ||
                  roleFilter.length > 0 ||
                  (taskFieldFilter && taskFieldFilter.filters.length > 0)) && (
                  <span style={{ marginLeft: '4px' }}>
                    (
                    {activityFilter.length + roleFilter.length + (taskFieldFilter ? taskFieldFilter.filters.length : 0)}
                    )
                  </span>
                )}
              </TabletButton>
            </TooltipMenu>

            <TabletButton
              size='small'
              aria-controls='options-menu'
              aria-haspopup='true'
              onClick={() => {
                reset();
              }}
              startIcon={<Refresh />}
              style={{
                height: '100%',
                border: 'none',
                boxSizing: 'border-box',
                padding: '2px 7px',
                marginRight: '10px',
                color: theme.color.grey.neutral300,
              }}
            >
              <strong>Reset</strong>
            </TabletButton>
          </Box>
        </Box>
        <Box
          style={{ width: '98%', marginLeft: 'auto', marginRight: 'auto', overflowX: 'auto', flex: 1, minHeight: 0 }}
        >
          <DataGrid
            columns={columns}
            pagination
            pageSize={25}
            rows={incomingVolunteersData?.incomingVolunteers.map((v) => ({ ...v, id: v.incomingVolunteerId })) ?? []}
            loading={loading}
            page={currentPage}
            rowsPerPageOptions={[25]}
            disableSelectionOnClick
            rowCount={incomingVolunteersData?.total}
            rowHeight={70}
            headerHeight={30}
            onPageChange={(e) => {
              changePage(e);
            }}
            onRowClick={(params) => {
              handleRowClick(params.row as IncomingVolunteerSimple);
            }}
            components={{
              NoRowsOverlay: () => (
                <Box
                  height='100%'
                  width='100%'
                  alignItems='center'
                  justifyContent='center'
                  display={'flex'}
                  flexDirection='column'
                  style={{ color: '#464646' }}
                >
                  {error ? (
                    <>
                      <Warning style={{ fontSize: '48px', color: '#ff9800', marginBottom: '16px' }} />
                      <Typography variant='h6' style={{ marginBottom: '8px', color: '#ff9800' }}>
                        Warning
                      </Typography>
                      <Typography variant='body2' style={{ textAlign: 'center', maxWidth: '400px' }}>
                        {error}
                      </Typography>
                      <Button
                        onClick={() => fetch()}
                        style={{ marginTop: '16px', textTransform: 'none' }}
                        variant='outlined'
                        size='small'
                      >
                        Try Again
                      </Button>
                    </>
                  ) : (
                    <>
                      <span role='img' aria-label="Can't find" style={{ fontSize: '24px', marginBottom: '8px' }}>
                        😕
                      </span>
                      No results found
                    </>
                  )}
                </Box>
              ),
              NoResultsOverlay: () => (
                <Box
                  height='100%'
                  width='100%'
                  alignItems='center'
                  justifyContent='center'
                  display={'flex'}
                  style={{ color: '#464646' }}
                >
                  <span role='img' aria-label="Can't find">
                    😕&nbsp;&nbsp;&nbsp;
                  </span>
                  No results found
                </Box>
              ),
            }}
            className={classes.hideRightSeparator}
            paginationMode='server'
            style={{
              fontSize: '14px',
              border: 'none',
              cursor: 'pointer',
            }}
          />
        </Box>
      </Box>

      {/* Exports Popover */}
      <Popover
        open={Boolean(exportsPopoverAnchor)}
        anchorEl={exportsPopoverAnchor}
        onClose={handleExportsPopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 600,
            padding: '8px',
          },
        }}
      >
        <ExportsList exportType='incoming volunteers' onCreate={handleExportRequest} />
      </Popover>

      <TaskFieldSearchDialog
        open={taskFieldSearchOpen}
        onClose={() => setTaskFieldSearchOpen(false)}
        onSearch={handleTaskFieldSearch}
        initialFilters={taskFieldFilter ?? undefined}
        tasksData={tasksWithFieldsData}
        cakeTypeFilter='required'
      />
    </>
  );
});

export default IncomingVolunteersScreenSimple;
